/**
 * The IP arrangement's words and arithmetic, as pure functions — Story #5648
 * build v2 (C2, C1, S5).
 *
 * Same shape and same reason as sharingModel.ts beside it: the components own
 * the rendering, these functions own the meaning, and the tests exercise them
 * with fixtures shaped exactly like the live API's responses. Keeping them out
 * of the .tsx files is also what keeps React Fast Refresh working — a component
 * file that also exports constants loses it.
 *
 * THE RULE these encode: an arrangement is a RECORD, NOT A DIAL. Nothing here
 * decides what anybody can see. Every sentence below is about what was agreed
 * off the platform and who has acknowledged it — never about access.
 */
import type { IpArrangement, IpArrangementHolderRow } from '../../types/ipArrangements'

/** The sentence an inventor confirms before their acknowledgement is recorded.
 *
 * It has its own words on purpose: the literal "I understand" already carries a
 * very different act on this platform — lifting folder exclusions for a
 * portfolio — so acknowledging a legal record must not reuse it. The
 * organisation reads the mirror sentence (PROPOSE_CONFIRMATION) beside Propose,
 * so both sides read one definition of what they are doing.
 */
export const ACKNOWLEDGE_CONFIRMATION =
  'I confirm this arrangement is on record. This does not transfer ownership on '
  + 'InnovateFlow, and it does not replace the signed agreement.'

/** The organisation's half of the same sentence, shown beside Propose. */
export const PROPOSE_CONFIRMATION =
  'This records an arrangement made outside the platform. It does not transfer '
  + 'ownership on InnovateFlow, and it does not replace the signed agreement.'

/** A record still in play. History is everything else. */
export const LIVE_STATUSES = new Set(['proposed', 'acknowledged'])

/** "Option", "Licence", "Assignment" — the figure's own capitalisation. */
export function kindLabel(kind: string): string {
  return kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : ''
}

/**
 * "you and 2 other owners must each acknowledge" — the figure's sentence, and
 * an honest one when the caller is the only owner. Every owner of a co-owned
 * invention must acknowledge before the record reads as binding, which is why
 * this counts people rather than just saying "acknowledge".
 */
export function ownersSentence(ownersRequired: number): string {
  const others = Math.max(0, ownersRequired - 1)
  if (others === 0) return 'you are the only owner — your acknowledgement completes it'
  return `you and ${others} other owner${others === 1 ? '' : 's'} must each acknowledge`
}

/** What has happened to this record, in plain words. Null when it is simply
 * proposed and the caller has not answered — the row's own line already says
 * everything there is to say in that state. */
export function statusSentence(a: IpArrangement): string | null {
  switch (a.status) {
    case 'acknowledged':
      return 'On record — every owner has acknowledged it.'
    case 'declined':
      return 'Declined — the proposal ended for every owner.'
    case 'disputed':
      return 'Disputed — the proposal ended for every owner.'
    case 'superseded':
      return 'Superseded by a later arrangement. It stays here as history.'
    default:
      return a.my_state === 'acknowledged'
        ? `You have acknowledged this. ${a.owners_pending} owner`
          + `${a.owners_pending === 1 ? '' : 's'} still to answer.`
        : null
  }
}

/**
 * The grid pill's words, or null when this project should carry none.
 *
 * Only a LIVE record gets a pill: a declined, disputed or superseded
 * arrangement is history, and history does not belong on a row whose job is to
 * say what is true right now.
 */
export function ipPillLabel(arrangement: IpArrangement | undefined): string | null {
  if (!arrangement) return null
  if (arrangement.status === 'proposed') return `IP: ${arrangement.kind} proposed`
  if (arrangement.status === 'acknowledged') return `IP: ${arrangement.kind}, acknowledged`
  return null
}

/** The grid pill's tooltip: who proposed it, and what is waiting. */
export function ipPillTitle(arrangement: IpArrangement): string {
  const kind = kindLabel(arrangement.kind)
  const who = arrangement.proposed_by ?? 'an organisation'
  if (arrangement.status === 'acknowledged') {
    return `${kind} proposed by ${who} — every owner has acknowledged it. `
      + 'It changes nothing about what they can see.'
  }
  return arrangement.my_state === 'pending'
    ? `${kind} proposed by ${who} — awaiting your acknowledgement`
    : `${kind} proposed by ${who} — awaiting the other owners`
}

/**
 * The banner's sentence when a proposal is waiting on the caller — the C2
 * figure's ACKNOWLEDGE headline, word for word.
 */
export function acknowledgeHeadline(a: IpArrangement): string {
  return `${a.proposed_by ?? 'An organisation'} proposed an IP ${a.kind} on `
    + `'${a.project_name ?? 'a project of yours'}' — read it and acknowledge, or leave it.`
}

/** "2 of 3 owners have acknowledged" — the holder side's figure sentence. */
export function acknowledgementSentence(row: IpArrangementHolderRow): string {
  const one = row.owners_required === 1
  return `${row.owners_acknowledged} of ${row.owners_required} owner${one ? '' : 's'} `
    + `${one ? 'has' : 'have'} acknowledged`
}

/** The one live record to show on a header, or null. Newest first wins. */
export function currentArrangement(
  arrangements: IpArrangementHolderRow[],
): IpArrangementHolderRow | null {
  const live = arrangements.filter((a) => LIVE_STATUSES.has(a.status))
  if (live.length === 0) return null
  return [...live].sort((a, b) =>
    (b.proposed_at ?? '').localeCompare(a.proposed_at ?? ''))[0]
}

/**
 * The live record per project, for the grid's pill. Newest first, so a
 * superseding licence shows over the option it replaces.
 */
export function liveByProject(arrangements: IpArrangement[]): Record<string, IpArrangement> {
  const out: Record<string, IpArrangement> = {}
  const newestFirst = [...arrangements].sort(
    (x, y) => (y.proposed_at ?? '').localeCompare(x.proposed_at ?? ''))
  for (const a of newestFirst) {
    if (!LIVE_STATUSES.has(a.status)) continue
    if (!out[a.project_id]) out[a.project_id] = a
  }
  return out
}

/** The proposals still waiting on the CALLER personally, newest first. */
export function waitingOnMe(arrangements: IpArrangement[]): IpArrangement[] {
  return arrangements.filter((a) => a.status === 'proposed' && a.my_state === 'pending')
}
